import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { Plus, Trash2, Check, X, Star } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { product_id: "", name: "", rating: 5, title: "", body: "", verified: true, approved: true };

export default function AdminReviews() {
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [filterProduct, setFilterProduct] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // "", "pending", "approved"
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterProduct) params.product_id = filterProduct;
      if (filterStatus === "pending") params.approved = false;
      if (filterStatus === "approved") params.approved = true;
      const [r, p] = await Promise.all([
        api.get("/admin/reviews", { params }),
        products.length ? Promise.resolve({ data: products }) : api.get("/admin/products"),
      ]);
      setList(r.data);
      if (!products.length) setProducts(p.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterProduct, filterStatus]);

  const openNew = () => setDrawer({ review: { ...EMPTY, product_id: products[0]?.slug || "" }, isNew: true });
  const openEdit = (r) => setDrawer({ review: { ...EMPTY, ...r }, isNew: false });
  const close = () => setDrawer(null);

  const save = async (e) => {
    e.preventDefault();
    const r = drawer.review;
    setSaving(true);
    try {
      const payload = { ...r, rating: Number(r.rating) };
      if (drawer.isNew) { await api.post("/admin/reviews", payload); toast.success("Review created"); }
      else { await api.put(`/admin/reviews/${r.id}`, payload); toast.success("Updated"); }
      close(); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const approve = async (r) => {
    try { await api.post(`/admin/reviews/${r.id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (r) => {
    if (!window.confirm("Delete review?")) return;
    try { await api.delete(`/admin/reviews/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const productName = (pid) => products.find((p) => p.slug === pid || p.id === pid)?.name || pid;

  return (
    <div className="p-6 md:p-10" data-testid="admin-reviews">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="font-script text-3xl text-blush-400">social proof</span>
          <h1 className="font-heading text-3xl md:text-4xl text-ink-900">Reviews</h1>
        </div>
        <button onClick={openNew} className="btn-maroon inline-flex items-center gap-2" data-testid="review-new-btn">
          <Plus size={14} strokeWidth={1.6} /> Add review
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select value={filterProduct} onChange={(e) => setFilterProduct(e.target.value)} className="field !py-2 !px-3 text-sm max-w-xs">
          <option value="">All products</option>
          {products.map((p) => <option key={p.id} value={p.slug || p.id}>{p.name}</option>)}
        </select>
        <div className="flex items-center gap-1">
          {[{ id: "", label: "All" }, { id: "pending", label: "Pending" }, { id: "approved", label: "Approved" }].map((t) => (
            <button key={t.id} onClick={() => setFilterStatus(t.id)} data-testid={`review-filter-${t.id || 'all'}`}
              className={`px-4 h-9 rounded-full text-xs font-body uppercase tracking-[0.18em] border transition ${
                filterStatus === t.id ? "bg-blush-600 text-white border-blush-600" : "bg-white text-ink-700 border-blush-100 hover:border-blush-300"
              }`}>{t.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-ink-500 font-body">Loading…</div>
      ) : list.length === 0 ? (
        <div className="p-12 text-center bg-white border border-blush-100 rounded-3xl">
          <Star size={32} strokeWidth={1.2} className="mx-auto text-blush-400 mb-3" />
          <p className="font-heading text-xl text-ink-900">No reviews yet</p>
          <p className="mt-1 font-body text-ink-500 text-sm">Seed glowing ones for your new products from the button above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((r) => (
            <div key={r.id} className="rounded-3xl bg-white border border-blush-100 p-5" data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-lg text-ink-900">{r.name}</span>
                    {r.verified && <span className="text-[9px] uppercase tracking-[0.18em] bg-blush-50 text-blush-600 px-2 py-0.5 rounded-full font-body">Verified</span>}
                    {!r.approved && <span className="text-[9px] uppercase tracking-[0.18em] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-body">Pending</span>}
                  </div>
                  <p className="text-xs font-body text-ink-500 mt-1">{productName(r.product_id)}</p>
                </div>
                <div className="flex items-center gap-0.5 text-blush-600 shrink-0">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={13} strokeWidth={1.5} fill={i <= r.rating ? "currentColor" : "none"} />
                  ))}
                </div>
              </div>
              {r.title && <p className="mt-3 font-heading text-base text-ink-900">{r.title}</p>}
              <p className="mt-1 text-sm font-body text-ink-700 leading-relaxed whitespace-pre-line line-clamp-3">{r.body}</p>
              <div className="mt-4 flex items-center gap-2">
                {!r.approved && (
                  <button onClick={() => approve(r)} className="btn-maroon text-xs inline-flex items-center gap-1" data-testid={`review-approve-${r.id}`}>
                    <Check size={12} strokeWidth={1.8} /> Approve
                  </button>
                )}
                <button onClick={() => openEdit(r)} className="btn-ghost-rose text-xs">Edit</button>
                <button onClick={() => del(r)} className="h-8 w-8 grid place-items-center rounded-full text-blush-600 hover:bg-blush-50 ml-auto" aria-label="Delete" data-testid={`review-delete-${r.id}`}>
                  <Trash2 size={14} strokeWidth={1.6} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {drawer && (
        <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm" onClick={close}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="absolute inset-y-0 right-0 w-full sm:max-w-lg bg-cream-50 overflow-y-auto" data-testid="review-drawer">
            <div className="sticky top-0 bg-cream-50/95 backdrop-blur border-b border-blush-100 px-6 py-4 flex items-center justify-between">
              <h3 className="font-heading text-2xl text-ink-900">{drawer.isNew ? "New review" : "Edit review"}</h3>
              <button type="button" onClick={close} className="h-9 w-9 grid place-items-center rounded-full hover:bg-blush-50" aria-label="Close">
                <X size={18} strokeWidth={1.6} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Product">
                <select required value={drawer.review.product_id}
                  onChange={(e) => setDrawer((d) => ({ ...d, review: { ...d.review, product_id: e.target.value } }))}
                  className="field" data-testid="review-field-product">
                  <option value="">Select product…</option>
                  {products.map((p) => <option key={p.id} value={p.slug || p.id}>{p.name}</option>)}
                </select>
              </Field>
              <Field label="Reviewer name">
                <input required value={drawer.review.name}
                  onChange={(e) => setDrawer((d) => ({ ...d, review: { ...d.review, name: e.target.value } }))}
                  className="field" data-testid="review-field-name" />
              </Field>
              <Field label="Rating">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button key={i} type="button"
                      onClick={() => setDrawer((d) => ({ ...d, review: { ...d.review, rating: i } }))}
                      data-testid={`review-rating-${i}`}
                      className="h-10 w-10 grid place-items-center text-blush-600">
                      <Star size={22} strokeWidth={1.5} fill={i <= drawer.review.rating ? "currentColor" : "none"} />
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Title">
                <input value={drawer.review.title}
                  onChange={(e) => setDrawer((d) => ({ ...d, review: { ...d.review, title: e.target.value } }))}
                  className="field" placeholder="Absolutely stunning!" />
              </Field>
              <Field label="Body" required>
                <textarea required rows={5} value={drawer.review.body}
                  onChange={(e) => setDrawer((d) => ({ ...d, review: { ...d.review, body: e.target.value } }))}
                  className="field" />
              </Field>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 font-body text-sm text-ink-700 cursor-pointer">
                  <input type="checkbox" checked={drawer.review.verified}
                    onChange={(e) => setDrawer((d) => ({ ...d, review: { ...d.review, verified: e.target.checked } }))} />
                  Verified purchase
                </label>
                <label className="flex items-center gap-2 font-body text-sm text-ink-700 cursor-pointer">
                  <input type="checkbox" checked={drawer.review.approved}
                    onChange={(e) => setDrawer((d) => ({ ...d, review: { ...d.review, approved: e.target.checked } }))} />
                  Approved (visible on site)
                </label>
              </div>
              <div className="pt-4 flex items-center justify-end gap-2">
                <button type="button" onClick={close} className="btn-ghost-rose text-xs">Cancel</button>
                <button type="submit" disabled={saving} className="btn-maroon text-xs" data-testid="review-save-btn">
                  {saving ? "Saving…" : drawer.isNew ? "Create" : "Save"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] font-body text-ink-500 mb-2">
        {label}{required && <span className="text-blush-600"> *</span>}
      </span>
      {children}
    </label>
  );
}
